import { BoxStatusFragment, Unlock_Request_Type_Enum } from 'generated/graphql';
import { SharedProps, useSnackbar } from 'notistack';
import React, { ReactNode, useCallback } from 'react';
import { BoxStatus } from './useSubBoxStatuses';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { Label } from 'labels';

const isUnlockRequest = (newData: BoxStatusFragment) =>
  newData.request_status === Unlock_Request_Type_Enum.Request;

const isNewRequest = (
  prevData: BoxStatus | undefined,
  newData: BoxStatusFragment
) => {
  return prevData && prevData.requestStatus !== newData.request_status;
};

const getVariant = (newData: BoxStatusFragment): SharedProps['variant'] => {
  switch (newData.request_status) {
    case Unlock_Request_Type_Enum.Request:
      return 'info';
    case Unlock_Request_Type_Enum.ReceiveError:
    case Unlock_Request_Type_Enum.UnlockError:
    case Unlock_Request_Type_Enum.Timeout:
      return 'error';
    case Unlock_Request_Type_Enum.Success:
      return 'success';
  }
  return 'default';
};

const useStyles = makeStyles((theme: Theme) => ({
  progress: { marginLeft: 10 },
}));

const Message = ({ data }: { data: BoxStatusFragment }) => {
  const classes = useStyles();
  const { box, request_status, is_unlocked } = data;

  let message: ReactNode = '';

  switch (request_status) {
    case Unlock_Request_Type_Enum.Success:
      message = <Typography>コマンド受信成功</Typography>;
      break;
    case Unlock_Request_Type_Enum.Request:
      message = (
        <Box display="flex">
          {`${is_unlocked ? Label.KeyClose : Label.KeyOpen}通信中`}
          <CircularProgress
            size={20}
            className={classes.progress}
            color="inherit"
          />
        </Box>
      );
      break;
    case Unlock_Request_Type_Enum.ReceiveError:
      message = <Typography>コマンド受信エラー</Typography>;
      break;

    case Unlock_Request_Type_Enum.UnlockError:
      message = (
        <Typography>{`${
          is_unlocked ? Label.KeyClose : Label.KeyOpen
        } エラー`}</Typography>
      );
      break;

    case Unlock_Request_Type_Enum.Timeout:
      message = <Typography>コマンド受信タイムアウト</Typography>;
      break;
  }

  return (
    <Box display="grid" gridTemplateColumns="auto auto" gridColumnGap="20px">
      <Typography>{box.name}</Typography>
      <Typography>{box.imei}</Typography>
      <Box gridColumn="span 2" justifySelf="center">
        {message}
      </Box>
    </Box>
  );
};

export const useStatusSnack = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const displaySnack = useCallback(
    (prevData: BoxStatus | undefined, newData: BoxStatusFragment) => {
      const { box_id, request_status } = newData;
      if (isNewRequest(prevData, newData) || isUnlockRequest(newData)) {
        switch (request_status) {
          case Unlock_Request_Type_Enum.Request:
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.UnlockError}`);
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.ReceiveError}`);
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.Timeout}`);
            break;

          case Unlock_Request_Type_Enum.Success:
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.UnlockError}`);
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.ReceiveError}`);
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.Timeout}`);
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.Request}`);
            break;

          default:
            closeSnackbar(`${box_id}${Unlock_Request_Type_Enum.Request}`);
            break;
        }

        enqueueSnackbar(<Message data={newData} />, {
          key: `${box_id}${request_status}`,
          preventDuplicate: true,
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
          autoHideDuration: 15000,
          variant: getVariant(newData),
        });
      }
    },
    [enqueueSnackbar, closeSnackbar]
  );

  return displaySnack;
};
